/* eslint-disable react-hooks/exhaustive-deps */
import {
  useEffect,
  useState,
  MouseEvent,
  useMemo,
  useRef,
  useCallback,
} from 'react'
import { FilterDataType, ChatSidePanelHooks } from '@dataTypes/message.types'
import { CHANNEL_SELECTION } from '@constants/Message'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  ChatChannel,
  ChatDirection,
  IChatConversation,
  IChatMessage,
} from '@dataTypes/chat.type'
import {
  bulkUpdateChatMessages,
  bulkUpdateConversation,
  createConversation,
  updateSelectedMessage,
  updateConversation,
  addContactGroupParticipant,
  unblockUser,
} from '@store/actions'
import { MenuEventArgs } from '@syncfusion/ej2-react-navigations'
import { getComponent } from '@syncfusion/ej2-base'
import { CheckBox } from '@syncfusion/ej2-buttons'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { messageCountsAsUnread } from '@utils/chat.util'
import {
  SET_CONTACT_LIST,
  SET_CHANNEL,
  SET_CHAT_LIST,
  SET_PARTICIPANT,
  SET_SHOW_MODAL,
} from '@store/reducers/messageCenter.reducer'

function useChatSidePanel(): ChatSidePanelHooks {
  const { showToast } = useNotificationToast()
  const dispatch = useAppDispatch()
  const messagesState = useAppSelector((state) => state.chat.data) || []
  const messagesLoading = useAppSelector((state) => state.chat.loading)
  const selectedMessageState = useAppSelector(
    (state) => state.chat.selectedMessage,
  )
  const archivedList = useAppSelector((state) => state.chat.archived)
  const inboxList = useAppSelector((state) => state.chat.inbox)
  const contactGroups = useAppSelector((state) => state.contactGroup.data)
  const blockedUsers = useAppSelector((state) => state.blockedUser.data)
  // use states
  const [defaultConversation, setDefaultConversation] = useState<
    IChatConversation[]
  >(inboxList ?? [])
  const [showArchive, setShowArchive] = useState<boolean>(false)
  const [filteredText, setFilteredText] = useState<string>('')
  const [selectedChannel, setSelectedChannel] = useState<string>('SMS')
  const [messageData, setMessageData] = useState<IChatConversation[]>(
    inboxList ?? [],
  )
  const [hoverConversation, setHoverConversation] = useState<string>()
  const [selectedConversations, setSelectedConversations] = useState<
    string[] | null
  >(null)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  //useRef
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  // constants
  const multipleSelectAction = useMemo(() => {
    const firstSelectedConversation = selectedConversations?.[0]
    const isPinned = messagesState.find(
      (el) => el.chat_conversation_id === firstSelectedConversation,
    )?.is_pinned
    const isUnread = messagesState
      .find((el) => el.chat_conversation_id === firstSelectedConversation)
      ?.chat_message.some(messageCountsAsUnread)
    return [
      { id: 'archive', icon: 'mgc_archive_line', label: 'Archive' },
      isPinned
        ? { id: 'unpin', icon: 'mgc_pin_line', label: 'Unpin' }
        : { id: 'pin', icon: 'mgc_pin_fill', label: 'Pin' },
      isUnread
        ? {
            label: 'Mark read',
            icon: 'mgc_mail_fill',
            id: 'read',
          }
        : {
            label: 'Mark unread',
            icon: 'mgc_mail_open_fill',
            id: 'unread',
          },
      {
        id: 'new-chat',
        icon: 'mgc_message_4_fill',
        label: 'New conversation',
      },
      {
        id: 'delete',
        icon: 'mgc_delete_2_fill',
        label: 'Delete',
      },
      {
        id: 'close',
        icon: 'mgc_close_line',
        label: 'Close',
      },
    ]
  }, [selectedConversations])

  const handleSelectConversation = (id: string): void => {
    const _unreadMessages: string[] = (
      messagesState.find((el) => el.chat_conversation_id === id)
        ?.chat_message ?? []
    )
      .filter(messageCountsAsUnread)
      .map((message) => message.chat_message_id)
      .filter((mid): mid is string => mid !== undefined)

    if (_unreadMessages.length > 0) {
      dispatch(
        bulkUpdateChatMessages({
          ids: _unreadMessages,
          data: { is_read: true },
        }),
      )
      dispatch(updateSelectedMessage({ id }))
    } else dispatch(updateSelectedMessage({ id }))
  }

  const generateTimeDisplay = (timestampString: Date | null): string[] => {
    if (timestampString) {
      const timestamp = new Date(timestampString)
      const dateNow = new Date()
      const longDisplay = moment(timestamp).format('MMM D, YYYY - h:mm A')

      if (dateNow.getFullYear() === timestamp.getFullYear()) {
        const dateDifference = dateNow.getDate() - timestamp.getDate()
        if (timestamp.getMonth() == dateNow.getMonth() && dateDifference <= 6) {
          if (
            moment(dateNow).format('MM-DD-YY') ===
            moment(timestamp).format('MM-DD-YY')
          ) {
            const minuteDifference = Math.floor(
              (dateNow.getTime() - timestamp.getTime()) / 60000,
            )
            if (minuteDifference < 1) {
              return ['Just now', longDisplay]
            } else if (minuteDifference === 1)
              return [`1 minute ago`, longDisplay]
            else if (minuteDifference < 60)
              return [`${minuteDifference} minutes ago`, longDisplay]
            else if (Math.floor(minuteDifference / 60) === 1)
              return [`1 hour ago`, longDisplay]
            else
              return [
                `${Math.floor(minuteDifference / 60)} hours ago`,
                longDisplay,
              ]
          } else return [moment(timestamp).format('ddd'), longDisplay]
        } else return [moment(timestamp).format('MMM DD'), longDisplay]
      } else return [moment(timestamp).format('MMM DD, YYYY'), longDisplay]
    } else return ['']
  }

  const getMessageDisplay = (
    conversation: IChatMessage[],
    searchText: string,
  ): IChatMessage => {
    const matchedMessage = conversation.find((convo) =>
      convo.content.toLowerCase().includes(searchText.toLowerCase()),
    )
    return matchedMessage && searchText.length
      ? matchedMessage
      : conversation[conversation.length - 1]
  }

  const handleFilter = (
    filterDataType: FilterDataType,
    filter: string,
  ): void => {
    switch (filterDataType) {
      case 'name':
        setFilteredText(filter)
        break
      case 'channel':
        setSelectedChannel(filter)
        break
    }
  }

  const onClickArchiveButton = (): void => {
    setShowArchive(!showArchive)
    dispatch(updateSelectedMessage())
  }

  const handleMultipleSelectAction = async (
    args: MouseEvent<HTMLElement>,
  ): Promise<void> => {
    const action = args.currentTarget.id
    if (selectedConversations && selectedConversations.length > 0) {
      switch (action) {
        case 'archive':
          {
            try {
              await dispatch(
                bulkUpdateConversation({
                  ids: selectedConversations ?? [],
                  data: { is_archived: true },
                }),
              ).unwrap()
              showToast(
                createSuccessToast(
                  'Chats archived',
                  `${selectedConversations.length} chat${selectedConversations.length > 1 ? 's' : ''} archived.`,
                ),
              )
            } catch {
              showToast(
                createFailedToast(
                  'Archiving failed',
                  'Archiving conversations failed. Please try again.',
                ),
              )
            }
          }
          break
        case 'pin':
          try {
            await dispatch(
              bulkUpdateConversation({
                ids: selectedConversations ?? [],
                data: { is_pinned: true },
              }),
            ).unwrap()
            showToast(createSuccessToast('Chat pinned', 'Conversation pinned.'))
          } catch {
            showToast(
              createFailedToast(
                'Pinning failed',
                'Pinning conversations failed. Please try again.',
              ),
            )
          }
          break
        case 'unpin':
          try {
            await dispatch(
              bulkUpdateConversation({
                ids: selectedConversations ?? [],
                data: { is_pinned: false },
              }),
            ).unwrap()
            showToast(
              createSuccessToast('Chat unpinned', 'Conversation unpinned.'),
            )
          } catch {
            showToast(
              createFailedToast(
                'Unpinning failed',
                'Unpinning conversations failed. Please try again.',
              ),
            )
          }
          break
        case 'read':
          try {
            await dispatch(
              bulkUpdateChatMessages({
                ids:
                  selectedConversations
                    ?.flatMap((id) =>
                      messagesState
                        .find((convo) => convo.chat_conversation_id === id)
                        ?.chat_message.map(
                          (message) => message.chat_message_id,
                        ),
                    )
                    .filter((id): id is string => id !== undefined) ?? [],
                data: { is_read: true },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat marked as read',
                'Conversation marked as read.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Mark as read failed',
                'Marking conversations as read failed. Please try again.',
              ),
            )
          }
          break
        case 'unread':
          try {
            await dispatch(
              bulkUpdateChatMessages({
                ids:
                  selectedConversations
                    ?.flatMap((id) =>
                      messagesState
                        .find((convo) => convo.chat_conversation_id === id)
                        ?.chat_message.filter(
                          (message) =>
                            message.chat_direction === ChatDirection.incoming,
                        )
                        .map((message) => message.chat_message_id),
                    )
                    .filter((id): id is string => id !== undefined) ?? [],
                data: { is_read: false },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat marked as unread',
                'Conversation marked as unread.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Mark as unread failed',
                'Marking conversations as unread failed. Please try again.',
              ),
            )
          }
          break
        case 'new-chat':
          {
            if (selectedConversations && selectedConversations.length > 0) {
              // Helper function to normalize and compare participant sets
              const normalizeParticipants = (
                participants: IChatConversation['chat_participant'],
              ): string => {
                return (
                  participants
                    ?.filter((p) => p?.contact_id)
                    .map((p) => p.contact_id)
                    .sort()
                    .join(',') || ''
                )
              }

              const selectedConversationData = selectedConversations?.map(
                (id) =>
                  messagesState.find(
                    (convo) => convo.chat_conversation_id === id,
                  ),
              )
              if (selectedConversations.length === 1)
                dispatch(
                  updateSelectedMessage({
                    id: selectedConversationData[0]
                      ?.chat_conversation_id as string,
                  }),
                )
              else {
                const channel = selectedConversationData[0]
                  ?.channel as ChatChannel
                const allParticipants = selectedConversationData
                  .filter(
                    (scd): scd is IChatConversation =>
                      scd !== undefined && scd.chat_participant !== undefined,
                  )
                  .flatMap((scd) => scd.chat_participant)
                  .filter(
                    (participant, index, self) =>
                      index ===
                      self.findIndex(
                        (p) =>
                          participant.contact_id &&
                          p.contact_id === participant.contact_id,
                      ),
                  )

                // Check if a conversation with the same participants already exists
                const newConversationParticipants =
                  normalizeParticipants(allParticipants)

                const existingConversation = messagesState.find((convo) => {
                  const convoParticipants = normalizeParticipants(
                    convo.chat_participant,
                  )
                  return (
                    convoParticipants === newConversationParticipants &&
                    convo.channel === channel
                  )
                })

                if (existingConversation) {
                  dispatch(
                    updateSelectedMessage({
                      id: existingConversation.chat_conversation_id as string,
                    }),
                  )
                } else {
                  dispatch(
                    createConversation({
                      conversation_data: {
                        channel,
                        chat_message: [],
                        chat_participant: allParticipants,
                      },
                      members_data: allParticipants.map((participant) => ({
                        ...participant,
                        chat_conversation_id: '',
                        chat_participant_id: undefined,
                      })),
                    }),
                  )
                }
              }
            }
          }
          break
        case 'delete':
          {
            dispatch(SET_CHAT_LIST(selectedConversations ?? []))
            dispatch(SET_SHOW_MODAL({ deleteChat: true }))
          }
          break
        default:
          break
      }
      if (action !== 'delete') setSelectedConversations([])
    }
    if (['close', 'new-chat', 'archive'].includes(action))
      setSelectedConversations(null)
  }

  const handleMainCheckboxChange = (): void => {
    if (
      selectedConversations &&
      selectedConversations.length !== messageData.length
    ) {
      setSelectedConversations(
        messageData
          .filter((d) => d.chat_conversation_id != undefined)
          .map((e) => e.chat_conversation_id as string),
      )
    } else {
      setSelectedConversations([])
    }
  }

  const handleClickConversationCheckbox = (id: string): void => {
    const current = selectedConversations ?? []

    if (!current.includes(id)) {
      setSelectedConversations([...current, id])
    } else {
      setSelectedConversations(current.filter((_id) => _id !== id))
    }
  }

  const handleMouseEnter = (id: string): void => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    debounceTimeout.current = setTimeout(() => {
      setHoverConversation(id)
    }, 50)
  }

  const handleMouseLeave = (conversationId?: string): void => {
    // Don't clear hover if dropdown is open for this conversation
    if (conversationId && openDropdownId === conversationId) {
      return
    }
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    debounceTimeout.current = setTimeout(() => {
      // Double check dropdown is still not open before clearing
      if (
        !openDropdownId ||
        (conversationId && openDropdownId !== conversationId)
      ) {
        setHoverConversation(undefined)
      }
    }, 50)
  }

  const handleDropdownOpen = useCallback((conversationId: string): void => {
    setOpenDropdownId(conversationId)
  }, [])

  const handleDropdownClose = useCallback((): void => {
    setOpenDropdownId(null)
    // Clear hover after a short delay to allow for smooth transition
    setTimeout(() => {
      setHoverConversation(undefined)
    }, 100)
  }, [])

  const handleDropdownAction = async (
    args: MenuEventArgs,
    conversation: IChatConversation,
  ): Promise<void> => {
    const action = args.item.id
    if (action && conversation.chat_conversation_id) {
      switch (action.toLowerCase()) {
        case 'archive':
          try {
            await dispatch(
              updateConversation({
                id: conversation.chat_conversation_id,
                data: {
                  is_archived: true,
                  date_time_archived_at: new Date(),
                },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat archived',
                'Conversation has been archived.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Archive failed',
                'Archiving conversation failed. Please try again.',
              ),
            )
          }
          break
        case 'unarchive':
          try {
            await dispatch(
              updateConversation({
                id: conversation.chat_conversation_id,
                data: {
                  is_archived: false,
                  date_time_archived_at: null,
                },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat unarchived',
                'Conversation has been restored from archive.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Unarchive failed',
                'Restoring conversation failed. Please try again.',
              ),
            )
          }
          break
        case 'pin':
          {
            try {
              await dispatch(
                updateConversation({
                  id: conversation.chat_conversation_id,
                  data: { is_pinned: true },
                }),
              ).unwrap()
              showToast(
                createSuccessToast('Chat pinned', 'Conversation pinned.'),
              )
            } catch {
              showToast(
                createFailedToast(
                  'Pinning failed',
                  'Pinning chat failed. Please try again.',
                ),
              )
            }
          }
          break
        case 'unpin':
          {
            try {
              await dispatch(
                updateConversation({
                  id: conversation.chat_conversation_id,
                  data: { is_pinned: false },
                }),
              ).unwrap()
              showToast(
                createSuccessToast('Chat unpinned', 'Conversation unpinned.'),
              )
            } catch {
              showToast(
                createFailedToast(
                  'Unpinning failed',
                  'Unpinning chat failed. Please try again.',
                ),
              )
            }
          }
          break
        case 'group':
          break
        case 'create_group':
          {
            const participant = conversation.chat_participant
            if (
              participant &&
              participant.length === 1 &&
              participant[0].contact
            ) {
              dispatch(SET_CONTACT_LIST([participant[0].contact]))
              if (conversation.channel)
                dispatch(SET_CHANNEL(conversation.channel))
              dispatch(SET_SHOW_MODAL({ group: true }))
            }
          }
          break
        case 'unread':
          try {
            await dispatch(
              bulkUpdateChatMessages({
                ids:
                  conversation.chat_message
                    .filter(
                      (message) =>
                        message.chat_direction === ChatDirection.incoming,
                    )
                    .map((message) => message.chat_message_id)
                    .filter((id): id is string => id !== undefined) ?? [],
                data: { is_read: false },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat marked as unread',
                'Conversation marked as unread.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Mark as unread failed',
                'Marking conversations as unread failed. Please try again.',
              ),
            )
          }
          break
        case 'read':
          try {
            await dispatch(
              bulkUpdateChatMessages({
                ids:
                  conversation.chat_message
                    .map((message) => message.chat_message_id)
                    .filter((id): id is string => id !== undefined) ?? [],
                data: { is_read: true },
              }),
            ).unwrap()
            showToast(
              createSuccessToast(
                'Chat marked as read',
                'Conversation marked as read.',
              ),
            )
          } catch {
            showToast(
              createFailedToast(
                'Mark as read failed',
                'Marking conversations as read failed. Please try again.',
              ),
            )
          }
          break
        case 'block':
          {
            const participant = conversation.chat_participant
            if (participant && participant.length > 0) {
              dispatch(SET_PARTICIPANT(participant[0]))
              dispatch(SET_SHOW_MODAL({ block: true }))
            }
          }
          break
        case 'unblock':
          {
            const participant = conversation.chat_participant
            if (!participant?.[0]?.contact?.contact_id) break
            const blockedUserId = blockedUsers.find(
              (blockedUser) =>
                blockedUser.contact_id === participant[0].contact?.contact_id,
            )?.blocked_user_id
            if (!blockedUserId) break
            try {
              await dispatch(unblockUser(blockedUserId)).unwrap()
              showToast(
                createSuccessToast(
                  'User unblocked',
                  'The contact has been unblocked and can message you again.',
                ),
              )
            } catch {
              showToast(
                createFailedToast(
                  'Unblock failed',
                  'Failed to unblock user. Please try again.',
                ),
              )
            }
          }
          break
        case 'delete':
          {
            dispatch(SET_CHAT_LIST([conversation.chat_conversation_id]))
            dispatch(SET_SHOW_MODAL({ deleteChat: true }))
          }
          break
        default:
          {
            const index = contactGroups.findIndex(
              (c) => c.contact_group_id === action,
            )
            if (
              index !== -1 &&
              conversation.chat_participant &&
              conversation.chat_participant.length === 1 &&
              conversation.chat_participant[0].contact?.contact_id
            ) {
              try {
                await dispatch(
                  addContactGroupParticipant({
                    contact_group_id: action,
                    contact_id:
                      conversation.chat_participant[0].contact?.contact_id,
                    created_by: '',
                    updated_by: '',
                  }),
                ).unwrap()
                showToast(
                  createSuccessToast(
                    'Added to group',
                    'Contact has been added to the group.',
                  ),
                )
              } catch {
                showToast(
                  createFailedToast(
                    'Add to group failed',
                    'Failed to add contact to group. Please try again.',
                  ),
                )
              }
            }
          }
          break
      }
    }
  }

  useEffect(() => {
    if (!messagesLoading && defaultConversation) {
      let filteredByName: IChatConversation[] = defaultConversation
      if (filteredText.length !== 0) {
        filteredByName = defaultConversation.filter((data) => {
          const participant = data.chat_participant
          const _chat = data.chat_message
          return (
            participant?.some((p) =>
              p.username
                ?.toLowerCase()
                .includes(filteredText.toLowerCase().trim()),
            ) ||
            data.name
              ?.toLowerCase()
              .includes(filteredText.toLowerCase().trim()) ||
            _chat?.some((convo) =>
              convo.content.toLowerCase().includes(filteredText.toLowerCase()),
            )
          )
        })
      }
      if (selectedChannel.toLowerCase() === 'all') {
        setMessageData(filteredByName)
      } else {
        const filteredByChannel = filteredByName.filter((data) =>
          data['channel'].toLowerCase().includes(selectedChannel.toLowerCase()),
        )
        setMessageData(filteredByChannel)
      }
    }
  }, [filteredText, selectedChannel, defaultConversation, messagesLoading])

  useEffect(() => {
    if (!archivedList || archivedList.length === 0) {
      setShowArchive(false)
    }
  }, [archivedList, inboxList])

  useEffect(() => {
    if (showArchive) {
      setDefaultConversation(archivedList ?? [])
    } else {
      setDefaultConversation(inboxList ?? [])
    }
  }, [showArchive, archivedList, inboxList])

  useEffect(() => {
    if (selectedMessageState) {
      const isArchived = selectedMessageState.is_archived
      if (isArchived !== showArchive) {
        dispatch(updateSelectedMessage())
      }
    }
  }, [selectedMessageState, showArchive])

  useEffect(() => {
    if (selectedConversations) {
      const allChecked =
        selectedConversations.length === messageData.length &&
        messageData.length > 0
      const someChecked =
        selectedConversations.length > 0 &&
        selectedConversations.length < messageData.length

      const checkboxElement = document.getElementById(
        'side-panel-main-checkbox',
      )

      if (checkboxElement) {
        const mainCheckbox = getComponent(
          checkboxElement,
          'checkbox',
        ) as CheckBox
        if (mainCheckbox) {
          mainCheckbox.checked = allChecked
          mainCheckbox.indeterminate = someChecked
        }
      }
    }
  }, [selectedConversations])

  useEffect(() => {
    if (selectedConversations) {
      const allExist = selectedConversations.every((selected) =>
        messageData.some((d) => d.chat_conversation_id === selected),
      )
      if (!allExist) {
        setSelectedConversations(null)
      }
    }
  }, [messageData])

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    if (!openDropdownId) return

    const handleClickOutside = (e: Event): void => {
      const target = e.target as HTMLElement

      // Check if click is inside Syncfusion dropdown popup
      const isInsideDropdownPopup =
        target.closest('.e-dropdown-popup') !== null ||
        target.closest('.e-popup') !== null ||
        target.closest('.mccsp-dropdown') !== null

      if (!isInsideDropdownPopup) {
        handleDropdownClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openDropdownId, handleDropdownClose])

  return {
    archivedList,
    blockedUsers,
    channelSelection: CHANNEL_SELECTION,
    contactGroups,
    data: messageData,
    filteredText,
    generateTimeDisplay,
    getMessageDisplay,
    handleClickConversationCheckbox,
    handleDropdownAction,
    handleDropdownClose,
    handleDropdownOpen,
    handleFilter,
    handleMainCheckboxChange,
    handleMouseEnter,
    handleMouseLeave,
    handleMultipleSelectAction,
    handleSelectConversation,
    hoverConversation,
    inboxList,
    multipleSelectAction,
    onClickArchiveButton,
    selectedChannel,
    selectedConversations,
    selectedMessageState,
    setHoverConversation,
    setSelectedConversations,
    setShowArchive,
    showArchive,
  }
}

export default useChatSidePanel
